import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, Wifi, Bell, Share2, Plus } from "lucide-react";
import useInstallPrompt from "@/hooks/useInstallPrompt";

const InstallAppBanner = () => {
  const { canInstall, installed, isIos, promptInstall } = useInstallPrompt();
  const [iosOpen, setIosOpen] = useState(false);

  // If app is already installed OR neither install path is available, hide entirely.
  if (installed || !canInstall) return null;

  const click = async () => {
    const result = await promptInstall();
    if (result === "ios") setIosOpen(true);
  };

  return (
    <>
      <section className="bg-[#1A2024] text-white relative overflow-hidden" data-testid="install-banner">
        <div className="absolute inset-0 ankara-stripes opacity-10 pointer-events-none" />
        <div className="container-narrow py-14 grid lg:grid-cols-12 gap-8 items-center relative">
          <div className="lg:col-span-7">
            <div className="label-overline text-[#D9923B] mb-3 inline-flex items-center gap-2">
              <Smartphone className="w-3.5 h-3.5" /> Get the app
            </div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">
              Same site. Just faster.
            </h2>
            <p className="mt-4 text-white/75 max-w-xl font-sans-ui leading-relaxed">
              Add AfriMarket to your home screen — opens instantly, works offline for things
              you&apos;ve already viewed, and the icon you tap is the Adinkra Knot, not a browser.
            </p>
            <ul className="mt-6 grid sm:grid-cols-3 gap-3 text-sm">
              <li className="flex items-center gap-2"><Download className="w-4 h-4 text-[#D9923B]"/> One-tap install</li>
              <li className="flex items-center gap-2"><Wifi className="w-4 h-4 text-[#D9923B]"/> Offline-ready</li>
              <li className="flex items-center gap-2"><Bell className="w-4 h-4 text-[#D9923B]"/> Full-screen, no browser bars</li>
            </ul>
            <div className="mt-7">
              <Button
                onClick={click}
                data-testid="install-banner-cta"
                className="bg-[#D9923B] hover:bg-[#D9923B]/90 text-[#1A2024] font-semibold rounded-md h-12 px-7"
              >
                <Download className="w-4 h-4 mr-2" />
                {isIos ? "How to install on iPhone" : "Install AfriMarket"}
              </Button>
            </div>
          </div>
          <div className="lg:col-span-5 flex justify-center">
            {/* Faux phone mockup */}
            <div className="relative w-[220px] h-[440px] rounded-[36px] bg-[#0F1419] border border-white/10 shadow-2xl overflow-hidden">
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full" />
              <div className="absolute inset-0 mt-8 mx-3 mb-3 rounded-[28px] bg-[#FBF6EE] flex flex-col">
                <div className="bg-[#1A2024] text-white p-3 flex items-center gap-2">
                  <img src="/logomark.png" alt="AfriMarket logo" className="w-7 h-7"/>
                  <span className="font-display text-sm">AfriMarket</span>
                </div>
                <div className="p-3 flex-1 space-y-2">
                  <div className="h-3 bg-black/10 rounded w-3/4" />
                  <div className="h-3 bg-black/10 rounded w-1/2" />
                  <div className="aspect-square bg-[#C05A3C]/10 rounded-lg mt-2 flex items-center justify-center">
                    <span className="text-[#C05A3C] text-xs font-semibold">Ankara dress · £45</span>
                  </div>
                  <div className="aspect-square bg-[#4A5D23]/10 rounded-lg flex items-center justify-center">
                    <span className="text-[#4A5D23] text-xs font-semibold">Suya catering · £80</span>
                  </div>
                </div>
                <div className="border-t border-black/5 p-3 flex justify-between text-[10px] text-[#5C5852]">
                  <span>Browse</span><span>Sell</span><span>Inbox</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* iOS modal — duplicated locally so the banner is self-contained */}
      {iosOpen && (
        <div
          data-testid="install-banner-ios-modal"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50"
          onClick={() => setIosOpen(false)}
        >
          <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 mx-auto rounded-xl bg-[#1A2024] flex items-center justify-center mb-3">
              <img src="/logomark.png" alt="AfriMarket logo" className="w-8 h-8" />
            </div>
            <div className="font-display text-2xl mb-2">Install on your iPhone</div>
            <p className="text-sm text-muted-foreground mb-4">Two taps and you&apos;re done — Safari only.</p>
            <ol className="text-left text-sm space-y-3 mb-5">
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C05A3C] text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Tap the <Share2 className="inline w-4 h-4 -mt-1 mx-1" /> <b>Share</b> button at the bottom of Safari.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-6 h-6 rounded-full bg-[#C05A3C] text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Scroll and pick <Plus className="inline w-4 h-4 -mt-1 mx-1" /> <b>Add to Home Screen</b>.</span>
              </li>
            </ol>
            <Button onClick={() => setIosOpen(false)} className="w-full bg-[#C05A3C] hover:bg-[#D46B4E] text-white rounded-md">Got it</Button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppBanner;
